import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { QueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { queryKeys } from '@/lib/query';
import type {
  Service,
  System,
  SystemCreate,
  SystemFacetsResponse,
  SystemFacetValue,
  SystemList,
  SystemOsVersionFacet,
  SystemRolesResponse,
  SystemScope,
  SystemUpdate,
  UpdatePolicy,
} from '@/types/api';

// ---------------------------------------------------------------------------
// S8-06: api.gen.ts ist regeneriert - role ist freier String (nullable) und
// die Inventar-Felder (fqdn, ip_addresses, managed, ansible_alias, vpn_ref,
// credentials_available, ...) kommen direkt aus den generierten Schemas.
// Die Aliase bleiben als stabile Namen für die Konsumenten bestehen.
// Regel 2 der Spec: vpn_ref ist NUR ein Dateiname, credentials_available NUR
// ein Flag, bw_item_id NUR die zentrale Item-Referenz (Lockerung 12.08.2026)
// — Secrets (.ovpn, Keys, bw CLI/BW_SESSION) bleiben am Sysadmin-PC.
// ---------------------------------------------------------------------------

// bw_item_id: Bitwarden-Item-UUID als zentrale REFERENZ (Regel 2 gelockert
// per Entscheid C. Urban 12.08.2026) — nur die ID liegt in der DB, bw CLI,
// BW_SESSION und Secrets bleiben am Sysadmin-PC. Seit dem gen:types-Nachzug
// kommt das Feld direkt aus den generierten Schemas; die Aliase bleiben als
// stabile Namen für die Konsumenten bestehen.
export type SystemDetail = System;

export type SystemCreateInput = SystemCreate;

/** PATCH-Body: alles optional, customer_id ist nicht änderbar. */
export type SystemUpdateInput = SystemUpdate;

export interface SystemsFilter {
  customerId?: string;
  locationId?: string;
  /** Freier String seit S8-06 (role-Literal-Einengung entfernt). */
  role?: string;
  updatePolicy?: UpdatePolicy;
  scope?: SystemScope;
  serviceType?: string;
  search?: string;
  cursor?: string;
  limit?: number;
}

export function useSystems(filter: SystemsFilter = {}): ReturnType<
  typeof useQuery<SystemList>
> {
  const params: Record<string, string | number | undefined> = {
    'filter[customer_id]': filter.customerId,
    'filter[location_id]': filter.locationId,
    'filter[role]': filter.role,
    'filter[update_policy]': filter.updatePolicy,
    'filter[scope]': filter.scope,
    'filter[service_type]': filter.serviceType,
    'filter[search]': filter.search,
    cursor: filter.cursor,
    limit: filter.limit ?? 50,
  };

  return useQuery<SystemList>({
    queryKey: queryKeys.systems.list(filter as Record<string, unknown>),
    queryFn: () => api.get<SystemList>('/systems', params),
  });
}

export function useSystem(
  id: string | undefined
): ReturnType<typeof useQuery<SystemDetail>> {
  return useQuery<SystemDetail>({
    queryKey: queryKeys.systems.detail(id ?? ''),
    queryFn: () => api.get<SystemDetail>(`/systems/${id ?? ''}`),
    enabled: Boolean(id),
  });
}

// --- Drift-Sicht (IST/SOLL) ------------------------------------------------
// GET /systems/{id}/services liefert seit dem Drift-Paket zusätzlich
// target_version (Soll aus version_targets), drift_status, observed_at und
// detection_method. api.gen.ts trägt die Felder inzwischen (openapi.yaml
// nachgezogen); ServiceWithDrift bleibt als benannter Kontrakt der Drift-
// Sicht bestehen — alle Felder optional, damit ältere Responses nichts
// brechen.

/** Backend-Mapping siehe routes.systems.drift_status_from_assessment. */
export type ServiceDriftStatus =
  | 'ok'
  | 'outdated_minor'
  | 'outdated_major'
  | 'unknown'
  | 'stale';

export interface ServiceWithDrift extends Service {
  /** Soll-Version aus version_targets zum service_type (null = kein Target gepflegt). */
  target_version?: string | null;
  /** IST/SOLL-Bewertung der jüngsten Observation ('stale' = älter als 7 Tage). */
  drift_status?: ServiceDriftStatus;
  /** Zeitpunkt der jüngsten Version-Observation (= observed_version_at). */
  observed_at?: string | null;
  /** Erfassungsweg der jüngsten Observation (z.B. 'ssh', 'zabbix'). */
  detection_method?: string | null;
}

export function useSystemServices(
  systemId: string | undefined
): ReturnType<typeof useQuery<ServiceWithDrift[]>> {
  return useQuery<ServiceWithDrift[]>({
    queryKey: queryKeys.systems.services(systemId ?? ''),
    queryFn: () => api.get<ServiceWithDrift[]>(`/systems/${systemId ?? ''}/services`),
    enabled: Boolean(systemId),
  });
}

// --- Rollen-Vorschläge (Spec S8-06, Kontrakt 2) ---------------------------

export type { SystemRoleCount, SystemRolesResponse } from '@/types/api';

/** GET /systems/roles — DISTINCT aus DB vereinigt mit der Basisliste. */
export function useSystemRoles(): ReturnType<
  typeof useQuery<SystemRolesResponse>
> {
  return useQuery<SystemRolesResponse>({
    queryKey: queryKeys.systems.roles,
    queryFn: () => api.get<SystemRolesResponse>('/systems/roles'),
  });
}

// Facet-Typen kommen seit dem openapi.yaml-Nachzug aus api.gen.ts; die
// Aliase werden - wie bei SystemRolesResponse - aus @/types/api importiert.
export type { SystemFacetValue, SystemOsVersionFacet, SystemFacetsResponse };

/** GET /systems/facets — DISTINCT Rolle / OS / VPN / Domain / Node; Kunde nach vorn. */
export function useSystemFacets(
  customerId?: string
): ReturnType<typeof useQuery<SystemFacetsResponse>> {
  return useQuery<SystemFacetsResponse>({
    queryKey: queryKeys.systems.facets(customerId),
    queryFn: () =>
      api.get<SystemFacetsResponse>('/systems/facets', {
        customer_id: customerId || undefined,
      }),
  });
}

// --- Mutationen (Spec S8-06 §5) --------------------------------------------
// Invalidation wie im Connection-Profile-Muster: kompletter systems-Baum
// (Listen, Detail, roles). Create/Delete zusätzlich customers-Baum, weil
// sich stats.systems_count verschiebt.

function invalidateSystems(qc: QueryClient): void {
  void qc.invalidateQueries({ queryKey: queryKeys.systems.all });
}

function invalidateSystemsAndCustomers(qc: QueryClient): void {
  invalidateSystems(qc);
  void qc.invalidateQueries({ queryKey: queryKeys.customers.all });
}

export function useCreateSystem(): ReturnType<
  typeof useMutation<SystemDetail, Error, SystemCreateInput>
> {
  const qc = useQueryClient();
  return useMutation<SystemDetail, Error, SystemCreateInput>({
    mutationFn: (body) => api.post<SystemDetail, SystemCreateInput>('/systems', body),
    onSuccess: () => invalidateSystemsAndCustomers(qc),
  });
}

export interface UpdateSystemInput {
  systemId: string;
  body: SystemUpdateInput;
}

export function useUpdateSystem(): ReturnType<
  typeof useMutation<SystemDetail, Error, UpdateSystemInput>
> {
  const qc = useQueryClient();
  return useMutation<SystemDetail, Error, UpdateSystemInput>({
    mutationFn: ({ systemId, body }) =>
      api.patch<SystemDetail, SystemUpdateInput>(`/systems/${systemId}`, body),
    onSuccess: () => invalidateSystems(qc),
  });
}

/** System löschen — Backend macht Soft-Delete (deleted_at). */
export function useDeleteSystem(): ReturnType<
  typeof useMutation<void, Error, string>
> {
  const qc = useQueryClient();
  return useMutation<void, Error, string>({
    mutationFn: (systemId) => api.delete(`/systems/${systemId}`),
    onSuccess: () => invalidateSystemsAndCustomers(qc),
  });
}

// useConnectionProfiles ist nach hooks/useConnectionProfiles.ts umgezogen
// (dort inkl. Create/Update/Delete-Mutationen).
